import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import type { DecomposeExportRow } from "./DecomposeClipSidebar";

type DecomposeClipActivityCardProps = {
    visible: boolean;
    exports: DecomposeExportRow[];
    step: "assign" | "crop" | "export" | "complete" | "error";
    error?: string | null;
    bottom?: number;
    right?: number;
    onOpen?: () => void;
};

const statusText = (row: DecomposeExportRow): string => {
    if (row.status === "complete") return "Done";
    if (row.status === "error") return "Failed";
    if (row.status === "uploading") return "Uploading";
    if (row.status === "exporting") return "Processing";
    return "Queued";
};

const DecomposeClipActivityCard: React.FC<DecomposeClipActivityCardProps> = ({
    visible,
    exports,
    step,
    error,
    bottom = 24,
    right = 24,
    onOpen,
}) => {
    if (!visible) return null;

    const total = exports.length;
    const doneCount = exports.filter((row) => row.status === "complete").length;
    const hasErrors = exports.some((row) => row.status === "error");
    const isProcessing = exports.some((row) => row.status === "exporting" || row.status === "uploading");

    const headerLabel = step === "complete"
        ? "Separate speakers complete"
        : step === "error"
            ? "Separate speakers issue"
            : "Separating speakers";
    const subLabel = total > 0 ? `${doneCount}/${total} done` : "Preparing";

    return (
        <Box
            sx={{
                position: "fixed",
                right,
                bottom,
                zIndex: 1390,
                width: "min(360px, calc(100vw - 32px))",
                minWidth: 280,
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                boxShadow: "0px 18px 50px rgba(15, 23, 42, 0.12)",
                p: "10px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {step === "complete" ? (
                    <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18, color: "#166534" }} />
                ) : step === "error" || hasErrors ? (
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: "#B91C1C" }} />
                ) : (
                    <CircularProgress size={16} thickness={6} sx={{ color: "#4B5563" }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#1F2937" }}>
                        {headerLabel}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#6B7280" }}>
                        {error ? error : subLabel}
                    </Typography>
                </Box>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={onOpen}
                    sx={{
                        textTransform: "none",
                        minWidth: "auto",
                        px: 1.25,
                        py: 0.35,
                        fontSize: 11.5,
                        borderColor: "#D1D5DB",
                        color: "#374151",
                        "&:hover": {
                            borderColor: "#9CA3AF",
                            backgroundColor: "#F9FAFB",
                        },
                    }}
                >
                    Open
                </Button>
            </Box>
            {exports.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "188px", overflowY: "auto", pr: "2px" }}>
                    {exports.map((row) => (
                        <Box
                            key={row.key}
                            sx={{
                                border: "1px solid #EEF2F7",
                                borderRadius: "8px",
                                px: "8px",
                                py: "6px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            {row.status === "exporting" || row.status === "uploading" ? (
                                <CircularProgress size={12} sx={{ color: "#6B7280", flexShrink: 0 }} />
                            ) : (
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "999px",
                                        backgroundColor: row.status === "complete" ? "#22C55E" : row.status === "error" ? "#EF4444" : "#D1D5DB",
                                        flexShrink: 0,
                                    }}
                                />
                            )}
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: "#1F2937",
                                    fontWeight: 500,
                                    flex: 1,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={row.speakerName}
                            >
                                {row.speakerName}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#6B7280", fontWeight: 500 }}>
                                {statusText(row)}
                                {Number.isFinite(row.progressPct) ? ` ${Math.round(Number(row.progressPct))}%` : ""}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            {isProcessing ? (
                <Typography sx={{ fontSize: 10.5, color: "#6B7280" }}>
                    Processing continues even if AI Tools is closed.
                </Typography>
            ) : null}
        </Box>
    );
};

export default DecomposeClipActivityCard;

